import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { easing } from "../../lib/animations";

/**
 * PROOF loader — precision-instrument aesthetic:
 *   - Pure black
 *   - PROOF logo centred (top half), fades out at 2s
 *   - Bottom: ultra-thin horizontal ruler with hundreds of tiny vertical ticks
 *   - Tiny vertical red marker glides from left → right
 *   - LARGE white counter `00 → 100` rides directly above the marker
 *   - Bar + counter + logo all hit 100 at exactly ~2.0s, then fade
 *   - Background `loader-bg.mp4` keeps playing — its last ~0.7s reveals the
 *     main page header by fading the loader veil out
 */
const BAR_DURATION = 2000; // ms — exact loading duration
const HEADER_REVEAL_BEFORE_END = 0.7; // s
const TICK_COUNT = 220;
const RULER_LEFT_PCT = 8;
const RULER_RIGHT_PCT = 92;

const RulerTicks = React.memo(() => {
    const ticks = [];
    for (let i = 0; i < TICK_COUNT; i++) {
        const isMajor = i % 20 === 0;
        const isMid = i % 10 === 0;
        const h = isMajor ? 18 : isMid ? 10 : 5;
        ticks.push(
            <div
                key={i}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: `${(i / (TICK_COUNT - 1)) * 100}%`,
                    bottom: 0,
                    width: 1,
                    height: h,
                    background: isMajor
                        ? "rgba(255,255,255,0.85)"
                        : "rgba(255,255,255,0.55)",
                    transform: "translateX(-0.5px)",
                }}
            />,
        );
    }
    return <>{ticks}</>;
});
RulerTicks.displayName = "RulerTicks";

export const LoadingScreen = ({ onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [logoVisible, setLogoVisible] = useState(true);
    const [veilVisible, setVeilVisible] = useState(true);
    const [completedFired, setCompletedFired] = useState(false);
    const videoRef = useRef(null);

    // Progress: linear 0 → 100 over BAR_DURATION (mechanical, no playful easing)
    useEffect(() => {
        document.body.classList.add("loading");
        const start = performance.now();
        let raf;
        const tick = () => {
            const elapsed = performance.now() - start;
            const t = Math.min(elapsed / BAR_DURATION, 1);
            setProgress(Math.round(t * 100));
            if (t < 1) {
                raf = requestAnimationFrame(tick);
            } else {
                // Logo + bar + counter fade together after a brief dwell at 100
                setTimeout(() => setLogoVisible(false), 320);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            document.body.classList.remove("loading");
        };
    }, []);

    // Background video: reveal header on last frames; safety net at 8s
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const onTimeUpdate = () => {
            if (!isFinite(v.duration) || v.duration <= 0) return;
            const remaining = v.duration - v.currentTime;
            if (remaining < HEADER_REVEAL_BEFORE_END && !completedFired) {
                setCompletedFired(true);
                onComplete && onComplete();
                setVeilVisible(false);
            }
        };
        const onEnded = () => {
            document.body.classList.remove("loading");
            setVeilVisible(false);
            if (!completedFired) {
                setCompletedFired(true);
                onComplete && onComplete();
            }
        };
        v.addEventListener("timeupdate", onTimeUpdate);
        v.addEventListener("ended", onEnded);
        const safety = setTimeout(() => {
            if (!completedFired) {
                setCompletedFired(true);
                onComplete && onComplete();
                setVeilVisible(false);
            }
        }, 8000);
        return () => {
            v.removeEventListener("timeupdate", onTimeUpdate);
            v.removeEventListener("ended", onEnded);
            clearTimeout(safety);
        };
    }, [onComplete, completedFired]);

    // The ruler spans from 8% to 92% — marker position follows the same mapping
    const markerPct =
        RULER_LEFT_PCT + (progress / 100) * (RULER_RIGHT_PCT - RULER_LEFT_PCT);

    return (
        <AnimatePresence>
            {veilVisible && (
                <motion.div
                    className="loading-screen"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.9, ease: easing.primary } }}
                    data-testid="loading-screen"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        background: "#000",
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    {/* Background video — original colour, no filter, plays in full */}
                    <video
                        ref={videoRef}
                        muted
                        autoPlay
                        playsInline
                        preload="auto"
                        data-testid="loading-video"
                        style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            zIndex: 1,
                        }}
                    >
                        <source src="/assets/yk/loader-bg.mp4" type="video/mp4" />
                    </video>
                    <div
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "radial-gradient(ellipse at center, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.32) 70%, rgba(0,0,0,0.55) 100%)",
                            zIndex: 2,
                        }}
                    />

                    {/* PROOF logo — centred upper area, fades at 2s */}
                    <AnimatePresence>
                        {logoVisible && (
                            <motion.div
                                key="logo"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    opacity: 0,
                                    y: -6,
                                    transition: { duration: 0.7, ease: easing.primary },
                                }}
                                transition={{ duration: 0.9, ease: easing.primary, delay: 0.05 }}
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    top: "38%",
                                    zIndex: 4,
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <img
                                    src="/assets/yk/proof-logo.png"
                                    alt="PROOF — Less Noise"
                                    draggable={false}
                                    data-testid="loading-logo"
                                    style={{
                                        width: "clamp(180px, 22vw, 320px)",
                                        height: "auto",
                                        filter: "drop-shadow(0 8px 36px rgba(0,0,0,0.55))",
                                        userSelect: "none",
                                    }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Large counter — rides above the marker */}
                    <AnimatePresence>
                        {logoVisible && (
                            <motion.div
                                key="counter"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{
                                    opacity: 0,
                                    transition: { duration: 0.55, ease: easing.primary },
                                }}
                                transition={{ duration: 0.6, delay: 0.45 }}
                                data-testid="loading-counter"
                                style={{
                                    position: "absolute",
                                    left: `${markerPct}%`,
                                    bottom: 78,
                                    zIndex: 5,
                                    transform: "translateX(-50%)",
                                    color: "#fff",
                                    fontFamily:
                                        "'Space Grotesk', 'Manrope', sans-serif",
                                    fontWeight: 300,
                                    fontSize: "clamp(36px, 4.4vw, 68px)",
                                    lineHeight: 0.9,
                                    letterSpacing: "-0.03em",
                                    whiteSpace: "nowrap",
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {progress < 100 ? String(progress).padStart(2, "0") : "100"}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Bottom ruler — thin baseline + tick array + glowing red marker */}
                    <AnimatePresence>
                        {logoVisible && (
                            <motion.div
                                key="ruler"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    opacity: 0,
                                    y: 6,
                                    transition: { duration: 0.55, ease: easing.primary },
                                }}
                                transition={{ duration: 0.7, delay: 0.35, ease: easing.primary }}
                                style={{
                                    position: "absolute",
                                    left: `${RULER_LEFT_PCT}%`,
                                    right: `${100 - RULER_RIGHT_PCT}%`,
                                    bottom: 36,
                                    height: 28,
                                    zIndex: 4,
                                }}
                            >
                                {/* baseline */}
                                <div
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        height: 1,
                                        background: "rgba(255,255,255,0.75)",
                                    }}
                                />
                                {/* tick array */}
                                <div
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        height: 18,
                                    }}
                                >
                                    <RulerTicks />
                                </div>
                                {/* glowing red marker */}
                                <div
                                    aria-hidden="true"
                                    data-testid="loading-marker"
                                    style={{
                                        position: "absolute",
                                        left: `${(progress / 100) * 100}%`,
                                        bottom: -4,
                                        width: 2,
                                        height: 28,
                                        background: "#6f0a14",
                                        boxShadow:
                                            "0 0 10px rgba(111,10,20,0.95), 0 0 22px rgba(111,10,20,0.55)",
                                        transform: "translateX(-1px)",
                                    }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Quiet top-left status tag */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: logoVisible ? 0.7 : 0 }}
                        transition={{ duration: 0.6, delay: 0.25 }}
                        style={{
                            position: "absolute",
                            left: 32,
                            top: 32,
                            zIndex: 4,
                            color: "rgba(255,255,255,0.7)",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                        }}
                    >
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <span
                                style={{
                                    width: 6,
                                    height: 6,
                                    background: "#6f0a14",
                                    display: "inline-block",
                                }}
                            />
                            Proof / Establishing signal
                        </span>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LoadingScreen;
